/**
 * The camera getting it wrong.
 *
 * DESIGN.md section 1. The misreads in this game are not a difficulty setting,
 * they are the citation:
 *
 * - **Roseville, California**: 1,427 Flock alerts over two years, **71% read
 *   the plate wrong.**
 * - **Institute for Justice**: at least 27 documented wrongful stops since
 *   2018, guns drawn in roughly two thirds, about a third machine error and
 *   two thirds human error.
 *
 * What is modeled here is only the machine error, and only its mechanism: a
 * character read as another character that looks like it.
 *
 * **The 71% is not set anywhere in this file and is not a target.** It is one
 * agency's figure over one period, it is probably near the bad end of the
 * range rather than the middle of it, and treating it as the number the
 * simulation ought to reproduce would be exactly the kind of overclaim the
 * project spends its credibility avoiding.
 *
 * What is set here is a small per-read error rate. Measured over two million
 * reads against the nationwide list in nationallist.ts, that produces:
 *
 *     per-read error    2%      6%      12%
 *     alerts wrong      1.4%    4.4%    9.6%
 *
 * And it does **not** move with how many plates are on the list, which was a
 * surprise worth keeping. True alerts and false ones both scale with the size
 * of the list, so the ratio cancels: the share of alerts that are wrong is
 * about `m / (1 - m)` and nothing else.
 *
 * Which means this model cannot produce Roseville's 71% at any believable
 * camera. It would need a per-read error rate around 41%, and no OCR is that
 * bad. **So character-level misreading is not the whole explanation**, and the
 * game should not pretend otherwise by turning the rate up until the number
 * matches.
 *
 * The likeliest missing piece is not an error at all: a plate read perfectly
 * can still raise an alert against an entry from **another state**. Iowa and
 * Minnesota both issue AAA 999, so the same seven characters are two different
 * cars, and a system that matches on the serial without the jurisdiction has a
 * false alert with no misread anywhere in it. That mechanism is documented,
 * costs nothing to add, and is the next thing to build here.
 *
 * The confusion set is the real one. Wisconsin does not issue I, O or Q on a
 * passenger plate precisely because they read as 1 and 0, so the pairs below
 * are what is left once the state has already removed the worst of it.
 */

import type { Rng } from './rng.ts';

/**
 * Characters that get read as each other on a plate.
 *
 * Grouped rather than paired, because the confusion is not symmetric in
 * practice and a group is the honest way to say "any of these could come back
 * as any other". Only characters a plate can actually carry appear.
 */
const CONFUSABLE: readonly string[] = [
  '0D',
  '1T7',
  '8B',
  '5S',
  '2Z',
  '6G',
  '4A',
  'MN',
  'VY',
  'CG',
  'PR',
  'KX',
  'EF',
  '9P',
  'HN',
  'JU',
];

const CONFUSION = new Map<string, string[]>();
for (const group of CONFUSABLE) {
  for (const character of group) {
    const others = [...group].filter((c) => c !== character);
    CONFUSION.set(character, [...(CONFUSION.get(character) ?? []), ...others]);
  }
}

/**
 * Chance that a single read comes back with at least one character wrong,
 * under good conditions.
 *
 * Vendors publish plate-level accuracy in the high nineties, and independent
 * testing does not agree with them. This sits between the two and it is a
 * tunable, not a finding.
 */
export const BASE_MISREAD_RATE = 0.06;

/**
 * How much worse it gets in the dark.
 *
 * A plate is read off a retroreflective surface by an infrared flash, which is
 * why night is not the problem people expect. Speed, weather and a dirty plate
 * are, and none of those are modeled yet, so this stands in for all of it.
 */
export const NIGHT_MISREAD_MULTIPLIER = 1.6;

/** Chance a misread garbles a second character as well as the first. */
const SECOND_CHARACTER_CHANCE = 0.22;

export interface Misread {
  /** What the camera decided it saw. */
  text: string;
  /** How many characters it got wrong. */
  wrong: number;
}

/**
 * What a camera reports having seen, given what was actually there.
 *
 * Returns the plate unchanged most of the time. When it does not, the returned
 * string is a plate somebody else might legitimately hold, which is the entire
 * problem: a wrong read is not obviously wrong, it is a different car.
 */
export function readPlate(rng: Rng, plate: string, rate = BASE_MISREAD_RATE): Misread {
  if (!rng.chance(rate)) return { text: plate, wrong: 0 };

  const characters = [...plate];
  // Only the characters that carry information. A hyphen or a space is not
  // going to be misread as a letter.
  const positions: number[] = [];
  for (let i = 0; i < characters.length; i++) {
    if (CONFUSION.has(characters[i] as string)) positions.push(i);
  }
  if (positions.length === 0) return { text: plate, wrong: 0 };

  let wrong = 0;
  const first = positions[rng.int(positions.length)] as number;
  characters[first] = swap(rng, characters[first] as string);
  wrong++;

  if (positions.length > 1 && rng.chance(SECOND_CHARACTER_CHANCE)) {
    let second = first;
    for (let attempt = 0; attempt < 8 && second === first; attempt++) {
      second = positions[rng.int(positions.length)] as number;
    }
    if (second !== first) {
      characters[second] = swap(rng, characters[second] as string);
      wrong++;
    }
  }

  return { text: characters.join(''), wrong };
}

function swap(rng: Rng, character: string): string {
  const options = CONFUSION.get(character);
  if (!options || options.length === 0) return character;
  return options[rng.int(options.length)] as string;
}

/**
 * Whether a read happened at night, given the world clock.
 *
 * Between ten at night and five in the morning, which is when a county is
 * empty enough that being on the road is conspicuous anyway.
 */
export function isNight(time: number, daySeconds: number): boolean {
  const hour = ((time % daySeconds) / daySeconds) * 24;
  return hour >= 22 || hour < 5;
}

/**
 * How much better or worse than average one installation is.
 *
 * CALIBRATION.md, having replaced Roseville's 71% with the measured cluster
 * around one-in-three, is explicit about the shape rather than the number:
 * *model it as a distribution, not a dial setting -- default around the
 * clustered value, let it vary by camera and by conditions, and treat Roseville
 * as the documented upper bound a bad installation can reach.*
 *
 * So this is per camera and deterministic from its id, because the thing that
 * makes one unit worse than another is where it is pointed, what the sun does
 * to it at four o'clock, and how long since anybody cleaned it -- none of which
 * change from read to read. A camera that is bad is bad all week.
 *
 * The range reaches Roseville at the top end and only there. A county gets a
 * few units that are genuinely poor, which is what a real deployment has, and
 * a player who works out *which* ones has learned something an operator would
 * have to run a report to know.
 */
export const CAMERA_QUALITY_MIN = 0.45;
export const CAMERA_QUALITY_MAX = 3.4;

export function cameraQuality(cameraId: string): number {
  let value = 0x811c9dc5;
  for (let i = 0; i < cameraId.length; i++) {
    value ^= cameraId.charCodeAt(i);
    value = Math.imul(value, 0x01000193) >>> 0;
  }
  const unit = (value >>> 0) / 0x100000000;
  // Skewed toward the good end: most installations are fine and a few are not,
  // which is the shape of every fleet of anything.
  const skewed = unit * unit;
  return CAMERA_QUALITY_MIN + skewed * (CAMERA_QUALITY_MAX - CAMERA_QUALITY_MIN);
}

/**
 * The error rate a camera is working at, right now.
 *
 * Base rate, times what the dark does to it, times how good this particular
 * unit is. Capped below one because a reader that got every plate wrong would
 * not be a reader.
 */
export function misreadRate(
  time: number,
  daySeconds: number,
  base = BASE_MISREAD_RATE,
  cameraId = '',
): number {
  const night = isNight(time, daySeconds) ? NIGHT_MISREAD_MULTIPLIER : 1;
  const quality = cameraId === '' ? 1 : cameraQuality(cameraId);
  return Math.min(0.9, base * night * quality);
}

/**
 * Every plate one character away from this one.
 *
 * Not used by the simulation. It exists so a test can ask how large the
 * confusion neighbourhood actually is, which is the number that decides how
 * often a misread lands on somebody who is on the hotlist.
 */
export function neighbors(plate: string): string[] {
  const out: string[] = [];
  const characters = [...plate];
  for (let i = 0; i < characters.length; i++) {
    for (const swapped of CONFUSION.get(characters[i] as string) ?? []) {
      const copy = [...characters];
      copy[i] = swapped;
      out.push(copy.join(''));
    }
  }
  return out;
}
